import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Baby, Check, CreditCard, Ruler, TrendingUp, X } from 'lucide-react';
import { subscriptionAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import Logo from '../components/Logo';

const Subscribe = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);

  useEffect(() => {
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    try {
      const status = await subscriptionAPI.getStatus();
      setSubscriptionStatus(status);
      if (status.has_subscription) {
        // Already subscribed, redirect to dashboard
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const checkout = await subscriptionAPI.createCheckout();
      if (checkout.url) {
        window.location.href = checkout.url;
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to start subscription. Please try again.',
        variant: 'destructive'
      });
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFF5F0] via-[#E5F5FF] to-[#F0FFE5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFB4A2]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5F0] via-[#E5F5FF] to-[#F0FFE5]">
      {/* Header */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Logo />
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            Subscribe to Measure
          </h1>
          <p className="text-xl text-gray-600">
            Get unlimited access to track your child's foot growth
          </p>
        </div>

        {/* Pricing Card */}
        <Card className="max-w-md mx-auto p-8 bg-white/90 backdrop-blur-sm border-2 border-[#FFB4A2]">
          <div className="text-center mb-6">
            <div className="inline-block px-4 py-1 bg-gradient-to-r from-[#FFB4A2] to-[#F7DBA7] text-white text-sm font-semibold rounded-full mb-4">
              MONTHLY SUBSCRIPTION
            </div>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-5xl font-bold text-gray-800">£2.99</span>
              <span className="text-gray-600">/month</span>
            </div>
          </div>

          <div className="space-y-4 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-gray-700">2 measurements per month</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-gray-700">AI-powered camera measurement</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-gray-700">Manual measurement option</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-gray-700">US, UK & EU size conversions</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-gray-700">Growth tracking & charts</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-gray-700">Unlimited children profiles</span>
            </div>
          </div>

          <Button
            onClick={handleSubscribe}
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#FFB4A2] via-[#F7DBA7] to-[#A8D5FF] hover:from-pink-600 hover:to-purple-600 text-white py-6 text-lg"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Processing...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Subscribe Now
              </div>
            )}
          </Button>

          <p className="text-center text-sm text-gray-500 mt-4">
            Cancel anytime. No commitment required.
          </p>
        </Card>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          <Card className="p-6 bg-white/80 text-center">
            <div className="w-12 h-12 bg-[#FFF5F0] rounded-full flex items-center justify-center mx-auto mb-4">
              <Ruler className="w-6 h-6 text-[#FFB4A2]" />
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">Accurate Sizing</h3>
            <p className="text-sm text-gray-600">Get precise US, UK & EU shoe size recommendations</p>
          </Card>
          
          <Card className="p-6 bg-white/80 text-center">
            <div className="w-12 h-12 bg-[#E5F5FF] rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-6 h-6 text-[#A8D5FF]" />
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">Track Growth</h3>
            <p className="text-sm text-gray-600">Monitor your child's foot growth over time</p>
          </Card>
          
          <Card className="p-6 bg-white/80 text-center">
            <div className="w-12 h-12 bg-[#F0FFE5] rounded-full flex items-center justify-center mx-auto mb-4">
              <Baby className="w-6 h-6 text-[#9ADFB4]" />
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">Multiple Children</h3>
            <p className="text-sm text-gray-600">Add unlimited children to your account</p>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Subscribe;
